import React from "react";
import { motion } from "framer-motion";
import { Repeat, CalendarClock, Car, TreePine, ArrowRight } from "lucide-react";
import { AnimatedLines } from "@/components/landing/AnimatedLines";

const PLANS = [
  {
    icon: Car,
    title: "Lavage mensuel véhicule",
    desc: "Entretien récurrent après un detailing complet pour préserver la brillance et la protection céramique.",
    tag: "Auto",
  },
  {
    icon: TreePine,
    title: "Passage trimestriel terrasse et extérieur",
    desc: "Démoussage et nettoyage réguliers pour empêcher le retour des mousses et prolonger la restauration.",
    tag: "Habitat",
  },
  {
    icon: CalendarClock,
    title: "Contrat sur-mesure",
    desc: "Une intervention d'urgence ou ponctuelle peut se prolonger en un plan de maintenance adapté à votre patrimoine.",
    tag: "Flexible",
  },
];

const CARD_HIDDEN = { opacity: 0, x: 30 };
const CARD_SHOW = { opacity: 1, x: 0 };
const CARD_VIEWPORT = { once: true };

export const Retention = ({ onReserve }) => {
  return (
    <section
      id="entretien"
      data-testid="retention-section"
      className="relative grain overflow-hidden bg-ink text-white py-20 md:py-28 scroll-mt-44"
    >
      <AnimatedLines variant="dark" />
      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-16 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-white/50">
              <Repeat className="w-4 h-4" /> L'abonnement
            </span>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-4 leading-[1.05]">
              L'Entretien Continu
            </h2>
            <p className="text-white/70 mt-5 text-base sm:text-lg leading-relaxed">
              Une surface restaurée mérite d'être préservée. Prolongez chaque intervention
              par un <span className="text-white font-semibold">contrat de maintenance</span> :
              votre patrimoine reste impeccable, sans mauvaise surprise ni dégradation
              coûteuse.
            </p>
            <button
              data-testid="retention-cta"
              onClick={() => onReserve("entretien")}
              className="cta mt-8 inline-flex items-center gap-2 bg-royal text-white font-extrabold px-7 py-4 rounded-sm hover:bg-royal-hover shadow-lg shadow-royal/30"
            >
              Activer mon abonnement
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>

          <div className="grid gap-4">
            {PLANS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={CARD_HIDDEN}
                whileInView={CARD_SHOW}
                viewport={CARD_VIEWPORT}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="flex gap-5 p-6 rounded-sm bg-ink-light border border-white/10 hover:border-white/40 transition-colors"
              >
                <span className="w-12 h-12 grid place-items-center bg-white/5 rounded-sm shrink-0">
                  <p.icon className="w-6 h-6 text-white" />
                </span>
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="font-heading font-bold text-lg">{p.title}</h3>
                    <span className="text-[10px] uppercase tracking-wider font-bold text-white/70 bg-white/10 px-2 py-0.5 rounded-sm">
                      {p.tag}
                    </span>
                  </div>
                  <p className="text-white/60 text-sm mt-1.5 leading-relaxed">{p.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
